import { DOCUMENT_CATEGORIES } from '../types';

/**
 * The Document Vault keeps its view state (category, page, card/list view) in the URL query,
 * e.g. `/document-vault?category=customers&page=3&view=card`.
 * Every view the user sees therefore has its own history entry, so the browser Back button
 * returns from a document details page to exactly the same category and page.
 */
export const VAULT_QUERY_PARAMS = {
  CATEGORY: 'category',
  PAGE: 'page',
  VIEW: 'view',
  /** Legacy param still used by links from other pages (signing, breadcrumbs, contract generator). */
  LEGACY_ACTIVE_TAB: 'activeTab',
} as const;

export const VAULT_LIST_VIEW = 'list';
/** The Document Vault page opens in the list view; the card view is opt-in (`?view=card`). */
export const VAULT_CARD_VIEW = 'card';

export const DOCUMENTS_PER_PAGE = 20;

export const VAULT_CATEGORIES: DOCUMENT_CATEGORIES[] = [
  'Business',
  'Employment',
  'Customers',
  'Suppliers',
];

export type VaultQueryState = {
  category: DOCUMENT_CATEGORIES;
  page: number;
  cardView: boolean;
};

export const parseVaultCategory = (value: string | null | undefined): DOCUMENT_CATEGORIES => {
  if (!value) {
    return 'All';
  }
  const normalized = value.trim().toLowerCase();
  return VAULT_CATEGORIES.find((category) => category.toLowerCase() === normalized) ?? 'All';
};

export const parseVaultPage = (value: string | null | undefined): number => {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 ? page : 1;
};

export const readVaultQuery = (params: URLSearchParams): VaultQueryState => ({
  category: parseVaultCategory(
    params.get(VAULT_QUERY_PARAMS.CATEGORY) ?? params.get(VAULT_QUERY_PARAMS.LEGACY_ACTIVE_TAB)
  ),
  page: parseVaultPage(params.get(VAULT_QUERY_PARAMS.PAGE)),
  // List view by default; old `?view=list` links keep working (and are normalized away).
  cardView: params.get(VAULT_QUERY_PARAMS.VIEW) === VAULT_CARD_VIEW,
});

/**
 * Returns a copy of `params` with the vault state written in its canonical form.
 * Default values are omitted to keep URLs short: no `category` for "All", no `page` for 1,
 * no `view` for the list view. Unrelated query params are preserved.
 */
export const writeVaultQuery = (
  params: URLSearchParams,
  state: VaultQueryState
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(VAULT_QUERY_PARAMS.LEGACY_ACTIVE_TAB);

  if (state.category === 'All') {
    next.delete(VAULT_QUERY_PARAMS.CATEGORY);
  } else {
    next.set(VAULT_QUERY_PARAMS.CATEGORY, state.category.toLowerCase());
  }

  if (state.page > 1) {
    next.set(VAULT_QUERY_PARAMS.PAGE, String(state.page));
  } else {
    next.delete(VAULT_QUERY_PARAMS.PAGE);
  }

  if (state.cardView) {
    next.set(VAULT_QUERY_PARAMS.VIEW, VAULT_CARD_VIEW);
  } else {
    next.delete(VAULT_QUERY_PARAMS.VIEW);
  }

  return next;
};
